import type { BrowserTakeoverStatusResp } from "@/types/browserTakeover";
import { browserConnectionEventSchema, browserConnectionAttemptSchema } from "@/types/browserTakeover";
import type { BrowserConnectionAttempt } from "@/types/browserTakeover";
import type { BrowserConnectionEvent } from "@/types/browserTakeover";
import { isCancellation } from "./retryPolicy.ts";
import { abortReason, throwIfAborted } from "./abortSignal.ts";

/**
 * Report an attempt without letting the report decide its outcome. `url_requested` is
 * raised before its try block and the failure events are raised inside a catch, so a throw
 * here would either escape unretried or replace the error being handled — surfacing as a
 * connection failure the user can only read as "the browser is unreachable".
 */
function report(
  progress: ((event: BrowserConnectionEvent) => void) | undefined,
  event: BrowserConnectionEvent,
): void {
  try {
    progress?.(event);
  } catch {
    // Losing a telemetry point is strictly better than losing the connection.
  }
}

/** Connection lookups may retry; website input never enters this operation. */
export async function requestBrowserConnection<T>(
  request: (signal: AbortSignal) => Promise<T>,
  progress?: (event: BrowserConnectionEvent) => void,
  signal?: AbortSignal,
  attempt?: BrowserConnectionAttempt,
): Promise<{ value: T; connection: BrowserConnectionAttempt }> {
  const connectionId = attempt?.connectionId ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const first = (attempt?.attemptNumber ?? 1) - 1;
  for (let index = first; index < (attempt ? first + 1 : 3); index++) {
    throwIfAborted(signal);
    const controller = new AbortController();
    let rejectCancelled: (reason: unknown) => void = () => {};
    const cancelled = new Promise<never>((_resolve, reject) => { rejectCancelled = reject; });
    const cancel = () => {
      // One reason object for both, or the polyfill path synthesises two different errors
      // for a single cancellation and the request's signal disagrees with the rejection.
      const reason = abortReason(signal);
      // React Native's `abort-controller` polyfill takes no argument here and drops the
      // reason, so on device this call only flips `aborted` — the reject below is what
      // carries why. On web the argument is honoured and reaches axios as the cancellation.
      controller.abort(reason);
      rejectCancelled(reason);
    };
    signal?.addEventListener("abort", cancel, { once: true });
    const deadlineAtMs = Math.min(attempt?.deadlineAtMs ?? Date.now() + 20000, Date.now() + 20000);
    const started = deadlineAtMs - 20000;
    const event = (phase: "url_requested" | "url_ready" | "unavailable") =>
      report(progress, {
        connectionId,
        phase,
        attemptNumber: index + 1,
        durationMs: Math.min(120000, Math.max(0, Date.now() - started)),
        ...(phase === "unavailable" ? { errorCode: "url_unavailable" as const } : {}),
      });
    event("url_requested");
    let deadline: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_resolve, reject) => {
      deadline = setTimeout(() => {
        controller.abort();
        reject(new Error("Browser connection lookup timed out"));
      }, Math.max(0, deadlineAtMs - Date.now()));
    });
    try {
      const result = await Promise.race([request(controller.signal), timeout, cancelled]);
      throwIfAborted(signal);
      event("url_ready");
      return { value: result, connection: { connectionId, attemptNumber: index + 1, deadlineAtMs } };
    } catch (error) {
      throwIfAborted(signal);
      // Before reporting: a cancelled lookup is not an unreachable browser, and the schema
      // has no phase that says so, so recording one would put the very claim this ticket
      // was about back into the metric. Nobody is waiting on the answer either, so stop
      // rather than spend the remaining attempts on it.
      if (isCancellation(error)) throw error;
      event("unavailable");
      const response =
        error && typeof error === "object" && "response" in error ? error.response : error;
      const status =
        response &&
        typeof response === "object" &&
        "status" in response &&
        typeof response.status === "number"
          ? response.status
          : undefined;
      // A 4xx other than 429 will say the same thing on the next attempt.
      const terminal = status !== undefined && status < 500 && status !== 429;
      if (attempt || index === 2 || terminal) throw error;
    } finally {
      clearTimeout(deadline);
      signal?.removeEventListener("abort", cancel);
      controller.abort();
    }
    await new Promise<void>((resolve, reject) => {
      const cancelDelay = () => {
        clearTimeout(timer);
        signal?.removeEventListener("abort", cancelDelay);
        reject(abortReason(signal));
      };
      const timer = setTimeout(() => {
        signal?.removeEventListener("abort", cancelDelay);
        resolve();
      }, (index + 1) * 1000);
      signal?.addEventListener("abort", cancelDelay, { once: true });
      if (signal?.aborted) cancelDelay();
    });
  }
  throw new Error("Browser connection is unavailable");
}

/** The same attempt budget covers URL lookup, signaling, playback and input readiness. */
export function browserConnectionAttemptUrl(value: string, connection: BrowserConnectionAttempt) {
  const url = new URL(value);
  const fragment = new URLSearchParams(url.hash.slice(1));
  fragment.set("connectionId", connection.connectionId);
  fragment.set("attemptNumber", String(connection.attemptNumber));
  fragment.set("deadlineAtMs", String(connection.deadlineAtMs));
  url.hash = fragment.toString();
  return url.toString();
}

export function browserWindowConnectionAttempt(value: unknown) {
  if (!value || typeof value !== "object" || !("connection" in value)) return;
  const parsed = browserConnectionAttemptSchema.safeParse(value.connection);
  return parsed.success ? parsed.data : undefined;
}

export function browserWindowAttemptFromUrl(value: string): BrowserConnectionAttempt {
  const fragment = new URLSearchParams(new URL(value).hash.slice(1));
  const parsed = browserConnectionAttemptSchema.safeParse({ connectionId: fragment.get("connectionId"), attemptNumber: Number(fragment.get("attemptNumber")), deadlineAtMs: Number(fragment.get("deadlineAtMs")) });
  return parsed.success ? parsed.data : { connectionId: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`, attemptNumber: 1, deadlineAtMs: Date.now() + 20000 };
}

export function browserWindowReady(value: unknown, url: string) {
  if (browserWindowAction(value) !== "ready") return false;
  const fragment = new URLSearchParams(new URL(url).hash.slice(1));
  if (!fragment.has("connectionId")) return true;
  const connection = browserWindowConnectionAttempt(value);
  return connection?.connectionId === fragment.get("connectionId") && connection.attemptNumber === Number(fragment.get("attemptNumber"));
}

/** The parent owns recovery when the viewer document has not loaded enough to run its bridge. */
export async function recoverBrowserWindowDocument(
  connection: BrowserConnectionAttempt,
  refresh: (connection: BrowserConnectionAttempt) => Promise<string>,
  signal: AbortSignal,
  progress?: (event: BrowserConnectionEvent) => void,
) {
  report(progress, { connectionId: connection.connectionId, attemptNumber: connection.attemptNumber, phase: "unavailable", durationMs: 20000, errorCode: "startup_timeout" });
  for (let index = connection.attemptNumber; index < 3; index++) {
    await new Promise<void>((resolve, reject) => {
      const cancel = () => { clearTimeout(timer); signal.removeEventListener("abort", cancel); reject(abortReason(signal)); };
      const timer = setTimeout(() => { signal.removeEventListener("abort", cancel); resolve(); }, index * 1000);
      signal.addEventListener("abort", cancel, { once: true });
      if (signal.aborted) cancel();
    });
    throwIfAborted(signal);
    const next = { connectionId: connection.connectionId, attemptNumber: index + 1, deadlineAtMs: Date.now() + 20000 };
    report(progress, { connectionId: next.connectionId, attemptNumber: next.attemptNumber, phase: "reconnecting", durationMs: 0, errorCode: "startup_timeout" });
    try {
      const url = await refresh(next);
      throwIfAborted(signal);
      return url;
    } catch (error) {
      throwIfAborted(signal);
      // Same rule as the lookup above, stated rather than inferred: `throwIfAborted` only
      // catches a cancellation that came from this signal, and a refresh can be cancelled
      // through its own.
      if (isCancellation(error)) throw error;
      report(progress, { connectionId: next.connectionId, attemptNumber: next.attemptNumber, phase: "unavailable", durationMs: Math.min(20000, Math.max(0, Date.now() - (next.deadlineAtMs - 20000))), errorCode: "url_unavailable" });
      if (index === 2) throw error;
    }
  }
  throw new Error("Browser document is unavailable");
}

export function browserConnectionEvent(value: unknown) {
  if (
    !value ||
    typeof value !== "object" ||
    !("type" in value) ||
    value.type !== "okthx:browser-window" ||
    !("action" in value) ||
    value.action !== "connection"
  )
    return;
  const parsed = browserConnectionEventSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

/** Kernel serves live views from regional hosts under these domains, on port 8443. */
const KERNEL_LIVE_VIEW_HOST = /\.(kernel\.sh|onkernel\.com)$/;

/**
 * Whether a link is a Kernel live view, the only thing the browser takeover may show.
 *
 * The link comes from our API, which reads it from Kernel, and it is a bearer capability
 * for a browser that may hold the user's sign-ins. The web frames it and iOS loads it in a
 * WebView pinned to its origin, so both check the same thing before showing it: https,
 * one of Kernel's live view hosts, port 8443. The web CSP's `frame-src` names the same
 * hosts.
 *
 * Shared so the two clients cannot drift on what they are willing to show.
 */
export function isKernelLiveViewUrl(value: string) {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  return (
    url.protocol === "https:" &&
    url.port === "8443" &&
    KERNEL_LIVE_VIEW_HOST.test(url.hostname) &&
    !url.username &&
    !url.password
  );
}

/**
 * How long the user keeps the browser, for the takeover bar: "12 min left", or
 * "Less than a minute left" once under a minute, when the next tick may end it.
 */
export function takeoverTimeLeft(expiresAtMs: number, nowMs: number) {
  const minutes = Math.floor((expiresAtMs - nowMs) / 60_000);
  return minutes < 1 ? "Less than a minute left" : `${minutes} min left`;
}

/** A finished chat turn can still hold a browser; the server decides whether it is available. */
export function shouldCheckBrowserTakeover(activityState: string): boolean {
  return [
    "running",
    "stopPending",
    "waitingForUser",
    "stopped",
    "waitingForExternalEvent",
  ].includes(activityState);
}

/** Only our isolated viewer pages may replace a provider view inside the app. */
export function isPrivateBrowserViewUrl(
  value: string,
  apiOrigin: string,
  page: "view" | "takeover" = "view",
): boolean {
  try {
    const url = new URL(value, apiOrigin);
    return (
      url.origin === new URL(apiOrigin).origin &&
      !url.username &&
      !url.password &&
      url.pathname === `/api/p/browser-auth/${page}`
    );
  } catch {
    return false;
  }
}

/** The API opts newer clients into the common window while older servers keep working. */
export function isBrowserWindowUrl(value: string, apiOrigin: string): boolean {
  return (
    isPrivateBrowserViewUrl(value, apiOrigin) &&
    new URL(value, apiOrigin).searchParams.get("window") === "1"
  );
}

export function browserWindowAction(
  value: unknown,
): "ready" | "close" | "done" | "end" | "take" | "refresh" | undefined {
  if (
    !value ||
    typeof value !== "object" ||
    !("type" in value) ||
    value.type !== "okthx:browser-window" ||
    !("action" in value)
  )
    return;
  const { action } = value;
  if (
    action === "ready" ||
    action === "close" ||
    action === "done" ||
    action === "end" ||
    action === "take" ||
    action === "refresh"
  )
    return action;
}

export function browserConnectionFromWindow(value: string, apiOrigin: string) {
  if (!isPrivateBrowserViewUrl(value, apiOrigin)) throw new Error("Invalid browser window");
  const fragment = new URLSearchParams(new URL(value, apiOrigin).hash.slice(1));
  const url = fragment.get("url");
  if (!url || !isKernelLiveViewUrl(url)) throw new Error("Invalid browser connection");
  const provider = new URL(url);
  if (provider.pathname.replace(/\/$/, "") !== "/browser/live" || !provider.searchParams.get("jwt"))
    throw new Error("Invalid browser connection");
  return {
    type: "okthx:browser-connection",
    url,
    mode: fragment.get("mode") === "watch" ? "watch" : "control",
    connection: browserConnectionAttemptSchema.safeParse({
      connectionId: fragment.get("connectionId"),
      attemptNumber: Number(fragment.get("attemptNumber")),
      deadlineAtMs: Number(fragment.get("deadlineAtMs")),
    }).data,
  };
}

/** Describe the user's next step rather than the browser's internal work record. */
export function browserTakeoverCopy(status: BrowserTakeoverStatusResp) {
  const signIn = status.signInArtifactId !== undefined;
  if (status.state === "user")
    return {
      title: signIn ? "Sign in to continue" : "okthx is paused — your turn",
      instruction: signIn
        ? "Sign in privately in the browser, then choose Continue with okthx."
        : "Complete the step requested in the conversation, then choose Continue with okthx.",
      takeLabel: "Open browser",
    };
  return {
    title: signIn ? "Sign in to continue" : "Browser",
    instruction: signIn ? "Open the browser to finish signing in privately." : undefined,
    takeLabel: signIn ? "Open browser" : "Take control",
  };
}

/**
 * The bar's offer to pick a released request back up. Shared so the sentence cannot drift
 * between the two apps; the label has two widths because the phone bar is one line.
 */
export const browserRestartCopy = {
  message: "The browser session expired.",
  label: "Start the browser again",
  shortLabel: "Start again",
  pendingLabel: "Starting…",
  failure: "Couldn’t start the browser again.",
} as const;
